import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import { portalApi } from "./portalApi";
import { CalendarDays, Pill, FileSignature, ListChecks, Bell, ChevronRight, Star } from "lucide-react";

const Stat = ({ icon: Icon, label, value, to, tone, testId }) => (
  <Link to={to} data-testid={testId} className="rounded-2xl bg-white p-4 border border-[#EFEBE0] hover:shadow-md transition-shadow flex items-center gap-3">
    <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}><Icon size={18} /></span>
    <div>
      <div className="font-heading text-xl font-bold text-[#3E2723]">{value}</div>
      <div className="text-[11px] font-semibold text-[#7B6157]">{label}</div>
    </div>
    <ChevronRight size={15} className="ml-auto text-[#D7CCC8]" />
  </Link>
);

export default function PortalDashboard() {
  const { me } = useOutletContext();
  const [d, setD] = useState(null);
  const [pendingSurveys, setPendingSurveys] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  useEffect(() => {
    portalApi.get("/portal/dashboard").then((r) => setD(r.data));
    portalApi.get("/portal/surveys/pending").then((r) => setPendingSurveys(r.data)).catch(() => {});
    portalApi.get("/portal/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading…</div>;
  const c = onboarding?.counts;
  const totalOnb = c ? c.tasks_open + c.tasks_done + c.consents_pending + c.consents_signed : 0;
  const doneOnb = c ? c.tasks_done + c.consents_signed : 0;
  const onbPct = totalOnb ? Math.round((doneOnb / totalOnb) * 100) : 0;
  return (
    <div className="space-y-5" data-testid="portal-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#3E2723]">Hello, {me.client.preferred_name || me.client.first_name} 👋</h1>
        <p className="text-sm text-[#7B6157]">Here's what's happening with your care.</p>
      </div>

      {onboarding && totalOnb > 0 && (
        <Link to="/portal/onboarding" data-testid="portal-onboarding-banner"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EFEBE0] bg-white p-4 hover:bg-[#FDFCF8] transition-colors">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#4E342E] text-white font-heading text-lg font-black">{onbPct}%</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#3E2723]">Onboarding: {doneOnb} of {totalOnb} completed</div>
            <div className="text-xs text-[#7B6157]">
              {c.consents_pending > 0 ? `${c.consents_pending} form${c.consents_pending > 1 ? "s" : ""} to sign` :
               c.tasks_open > 0 ? `${c.tasks_open} to-do${c.tasks_open > 1 ? "s" : ""} pending` : "Nice work, you are all caught up"}
            </div>
            <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-[#EFEBE0]">
              <div className="h-full bg-[#FFB300]" style={{ width: `${onbPct}%` }} />
            </div>
          </div>
          <ChevronRight size={16} className="text-[#D7CCC8]" />
        </Link>
      )}

      {pendingSurveys.length > 0 && (
        <Link to="/portal/feedback" data-testid="portal-survey-prompt"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#FFB300] bg-[#FFF8E1] p-4 hover:bg-[#FFECB3] transition-colors">
          <Star size={18} className="fill-[#FFB300] text-[#FFB300]" />
          <div>
            <div className="text-sm font-bold text-[#8D6E00]">How was your recent visit?</div>
            <div className="text-xs text-[#84675B]">{pendingSurveys.length} quick survey{pendingSurveys.length > 1 ? "s" : ""} waiting - takes 30 seconds</div>
          </div>
          <span className="ml-auto rounded-full bg-[#4E342E] px-4 py-1.5 text-xs font-bold text-white">Rate now</span>
        </Link>
      )}

      <div className="rounded-2xl bg-[#4E342E] text-white p-5" data-testid="portal-next-appt">
        <div className="text-[11px] uppercase font-bold text-white/50 mb-1">Next Appointment</div>
        {d.next_appointment ? (
          <div className="flex flex-wrap items-center gap-3">
            <CalendarDays size={22} className="text-[#FFB300]" />
            <div>
              <div className="font-heading text-lg font-bold">{dayjs(d.next_appointment.date).format("dddd, MMMM D")} · {d.next_appointment.start_time}</div>
              <div className="text-sm text-white/70">{d.next_appointment.type}</div>
            </div>
            <Link to="/portal/appointments" className="ml-auto rounded-full bg-[#FFB300] px-4 py-2 text-xs font-bold text-[#3E2723] hover:bg-[#FFC533]" data-testid="portal-view-appts-btn">View all</Link>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-white/70">Nothing scheduled yet.</span>
            <Link to="/portal/appointments" className="rounded-full bg-[#FFB300] px-4 py-2 text-xs font-bold text-[#3E2723] hover:bg-[#FFC533]" data-testid="portal-request-appt-cta">Request an appointment</Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Pill} label="Active medications" value={d.active_medications} to="/portal/medications" tone="bg-[#E0F2F1] text-[#00695C]" testId="portal-stat-meds" />
        <Stat icon={FileSignature} label="Forms to sign" value={d.pending_consents} to="/portal/documents" tone="bg-[#FFF3E0] text-[#8D5A00]" testId="portal-stat-consents" />
        <Stat icon={ListChecks} label="Open to-dos" value={d.open_todos} to="/portal/tasks" tone="bg-[#EDE7F6] text-[#5E35B1]" testId="portal-stat-todos" />
        <Stat icon={CalendarDays} label="Pending refills" value={d.pending_refills} to="/portal/medications" tone="bg-[#E3F2FD] text-[#1565C0]" testId="portal-stat-refills" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl bg-white border border-[#EFEBE0] p-5" data-testid="portal-upcoming-list">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">Upcoming Appointments</h3>
          {d.upcoming_appointments.length === 0 ? <p className="text-sm text-[#84675B]">No upcoming appointments.</p> : (
            <div className="space-y-2">
              {d.upcoming_appointments.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-xl bg-[#FDFCF8] px-3 py-2.5 text-sm">
                  <span className="font-bold text-[#4E342E] tabular-nums w-24">{dayjs(a.date).format("MMM D")} · {a.start_time}</span>
                  <span className="text-[#5D4037] truncate">{a.type}</span>
                  <span className="ml-auto rounded-full bg-[#EFEBE9] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41] capitalize">{(a.status || "").replace("_", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-[#EFEBE0] p-5" data-testid="portal-recent-notifications">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3 flex items-center gap-1.5"><Bell size={14} className="text-[#7B6157]" /> Recent Messages</h3>
          {d.recent_notifications.length === 0 ? <p className="text-sm text-[#84675B]">No messages yet.</p> : (
            <div className="space-y-2">
              {d.recent_notifications.map((n) => (
                <div key={n.id} className="rounded-xl bg-[#FDFCF8] px-3 py-2.5">
                  <div className="text-sm font-semibold text-[#3E2723]">{n.subject}</div>
                  <div className="text-xs text-[#7B6157] line-clamp-2">{n.message}</div>
                  <div className="text-[10px] text-[#84675B] mt-0.5">{dayjs(n.created_at).format("MMM D, h:mm A")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
